import { CommonModule } from "@angular/common";
import { Component, inject, signal } from "@angular/core";
import { Router, RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { ActivatedRoute } from "@angular/router";
import { QuizService } from "src/app/services/api/quiz.service";
import {
  CourseSuggestion,
  MyProfile,
  MyRecommendation,
  SkillNarrative,
} from "src/app/services/model/quiz.model";
import { ToastService } from "src/app/services/toast-service/toast.service";
import { SharedModule } from "src/shared/shared.module";

/**
 * The student's own skill picture — the whole reason this engine exists.
 *
 * A score tells a student nothing they can act on. This screen answers three
 * questions instead: which topics are strong, which are slipping, and what to
 * do about it. "What to do" is a real action, not advice: one press builds a
 * drill on exactly that topic, and where the topic maps to a chapter there is a
 * link straight back to the video.
 *
 * Mastery is recency-weighted on the server, so a topic someone has just fixed
 * stops being listed as weak instead of being dragged down by old attempts.
 */
@Component({
  selector: "app-my-skills",
  imports: [SharedModule, CommonModule, RouterModule, TranslateModule],
  templateUrl: "./my-skills.component.html",
  styleUrl: "./my-skills.component.scss",
})
export class MySkillsComponent {
  #quizService = inject(QuizService);
  #toastService = inject(ToastService);
  #router = inject(Router);

  isLoading = signal(true);
  profile = signal<MyProfile | null>(null);
  recommendations = signal<MyRecommendation[]>([]);
  /** The numbers written out. Always present — built from a template when no
   *  model is configured — so this block never renders empty. */
  narrative = signal<SkillNarrative | null>(null);
  /** Courses ranked by how much of what is missing they actually teach. */
  suggestions = signal<CourseSuggestion[]>([]);

  #route = inject(ActivatedRoute);
  /**
   * Whose report this is.
   *
   * A student reading their own leaves this unset. A parent must name the
   * child: the API refuses to guess, because a parent of two who omits it would
   * otherwise be shown the wrong child's report — and they would have no way to
   * tell.
   */
  studentId = signal<number | null>(null);
  /** Read-only framing when a parent is looking at somebody else's work. */
  viewingOther = signal(false);
  /** The topic whose drill is being built, so only that button spins. */
  buildingFor = signal<number | null>(null);

  ngOnInit(): void {
    // Subscribed, not snapshotted: a parent switching between children stays on
    // this route, so a one-shot read would leave the first child's report on
    // screen under the second child's name.
    this.#route.queryParamMap.subscribe((params) => {
      const raw = params.get("student_id");
      const id = raw ? Number(raw) : null;
      this.studentId.set(Number.isFinite(id as number) ? id : null);
      this.viewingOther.set(this.studentId() !== null);
      this.load();
    });
  }

  private load(): void {
    this.isLoading.set(true);
    const who = this.studentId() ?? undefined;

    this.#quizService.getProfile(who).subscribe({
      next: (profile) => {
        this.profile.set(profile);
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false),
    });

    this.#quizService.getRecommendations(who).subscribe({
      next: (rows) => this.recommendations.set(rows ?? []),
      error: () => this.recommendations.set([]),
    });

    this.#quizService.getNarrative(who).subscribe({
      next: (row) => this.narrative.set(row ?? null),
      error: () => this.narrative.set(null),
    });

    this.#quizService.getCourseSuggestions(who).subscribe({
      next: (rows) => this.suggestions.set(rows ?? []),
      error: () => this.suggestions.set([]),
    });
  }

  /**
   * Ability as something a person can read.
   *
   * Theta is the right number for the engine and the wrong number for a screen:
   * nobody knows what 0.8 logits means. This maps it to the chance of getting a
   * middling question right, which is what the scale is defined against and is
   * a sentence anyone can check against their own experience.
   */
  abilityPercent(theta?: number | null): number | null {
    if (theta === null || theta === undefined) return null;
    return Math.round((1 / (1 + Math.exp(-theta))) * 100);
  }

  /** Band number -> the word for it. Mirrors QuizQuestion::DIFFICULTY_LABELS;
   *  the API sends the label for the student's own level, but the two ends of
   *  the range come back as numbers. */
  levelLabel(level: number): string {
    return ["", "ง่ายมาก", "ง่าย", "ปานกลาง", "ยาก", "ยากมาก"][level] || "";
  }

  /** Colour one slice of the per-level breakdown on the same thresholds the
   *  mastery badges use, so the row reads as one picture. */
  sliceMastery(percent: number): string {
    if (percent >= 80) return "strong";
    if (percent >= 50) return "developing";
    return "weak";
  }

  blockedNames(row: MyRecommendation): string {
    return (row.blocked_by ?? []).map((b) => b.sub_lesson_name).join(", ");
  }

  /** The band a mastery level paints its bar in — see `masteryClass` for the
   *  badge form, still used by the per-difficulty chips. */
  masteryBand(mastery: string): string {
    switch (mastery) {
      case "strong":
        return "is-good";
      case "developing":
        return "is-mid";
      case "weak":
        return "is-low";
      default:
        return "is-none";
    }
  }

  masteryClass(mastery: string): string {
    switch (mastery) {
      case "strong":
        return "badge-light-success";
      case "developing":
        return "badge-light-warning";
      case "weak":
        return "badge-light-danger";
      default:
        return "badge-light";
    }
  }

  trendIcon(trend: string): string {
    if (trend === "up") return "↑";
    if (trend === "down") return "↓";
    return "→";
  }

  /** Build and open a drill on one topic. */
  practise(row: MyRecommendation): void {
    this.practiseTopic(row.sub_lesson_id);
  }

  /** The same action, reached from the review queue — where the row is a topic
   *  the student is GOOD at, so there is no recommendation to pass. */
  practiseTopic(subLessonId: number): void {
    if (this.buildingFor() !== null) return;
    this.buildingFor.set(subLessonId);

    this.#quizService.createPractice(subLessonId).subscribe({
      next: (quiz) => {
        this.buildingFor.set(null);
        this.#router.navigate(["/online-class/quiz", quiz.id]);
      },
      error: (err) => {
        this.buildingFor.set(null);
        this.#toastService.toastError(
          err?.error?.errors || "สร้างชุดฝึกไม่สำเร็จ",
        );
      },
    });
  }
}
